import * as React from 'react';
import {Bar, BarChart, CartesianGrid, LabelList, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import type {Dashboard} from '../../contract';
import {dayLabel, formatNumber, nearLimit} from '../../utilities/dashboard';
import {FIRST, GRID, INK_SECONDARY, SECOND, SURFACE} from './palette';
import {ChartTooltip} from './ChartTooltip';

// The dashboard's charts. All of them share one x axis (the days of the period), thin marks, a quiet grid, and a tooltip that lists
// every series at the day under the pointer.

const HEIGHT = 260;
const AXIS = {stroke: INK_SECONDARY, fontSize: 12, tickLine: false, axisLine: false} as const;

const dayAxis = (
    <XAxis dataKey="day" tickFormatter={dayLabel} interval="preserveStartEnd" minTickGap={28} {...AXIS} tick={{fill: INK_SECONDARY, fontSize: 12}}/>
);
const countAxis = (
    <YAxis allowDecimals={false} width={36} tickFormatter={formatNumber} {...AXIS} tick={{fill: INK_SECONDARY, fontSize: 12}}/>
);
const grid = <CartesianGrid vertical={false} stroke={GRID}/>;
// itemSorter={null}: keep the series in the order they were drawn (the default sorts them by name).
const legend = <Legend verticalAlign="top" align="left" height={28} iconType="plainline" itemSorter={null} wrapperStyle={{fontSize: 13, color: INK_SECONDARY}}/>;

// Names the line at its last point, so a reader does not have to go to the legend.
const endLabel = (name: string, last: number, color: string) => ({x, y, index}: { x?: number | string, y?: number | string, index?: number }) =>
    index === last ? <text x={Number(x) + 8} y={Number(y)} dy={4} fontSize={12} fill={INK_SECONDARY}>{name}</text> : <g/>;

const activeDot = {r: 5, stroke: SURFACE, strokeWidth: 2};

export function ActivePeopleChart({daily}: { daily: Dashboard['daily'] }) {
    return (
        <div role="img" aria-label="Line chart of the number of people who did anything in the app each day. The table view has the numbers.">
            <ResponsiveContainer width="100%" height={HEIGHT}>
                <LineChart data={daily} margin={{top: 8, right: 16, left: 0, bottom: 0}}>
                    {grid}{dayAxis}{countAxis}
                    <Tooltip content={<ChartTooltip/>} cursor={{stroke: INK_SECONDARY, strokeWidth: 1}}/>
                    <Line type="linear" dataKey="activePeople" name="Active people" stroke={FIRST} strokeWidth={2} dot={false} activeDot={activeDot} isAnimationActive={false}/>
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export function TasksChart({daily}: { daily: Dashboard['daily'] }) {
    const last = daily.length - 1;

    return (
        <div role="img" aria-label="Line chart of tasks created and tasks completed each day. The table view has the numbers.">
            <ResponsiveContainer width="100%" height={HEIGHT}>
                <LineChart data={daily} margin={{top: 8, right: 76, left: 0, bottom: 0}}>
                    {grid}{dayAxis}{countAxis}{legend}
                    <Tooltip content={<ChartTooltip/>} cursor={{stroke: INK_SECONDARY, strokeWidth: 1}}/>
                    <Line type="linear" dataKey="tasksCreated" name="Created" stroke={FIRST} strokeWidth={2} dot={false} activeDot={activeDot} isAnimationActive={false} label={endLabel('Created', last, FIRST)}/>
                    <Line type="linear" dataKey="tasksCompleted" name="Completed" stroke={SECOND} strokeWidth={2} dot={false} activeDot={activeDot} isAnimationActive={false} label={endLabel('Completed', last, SECOND)}/>
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export function SummariesChart({daily, limit}: { daily: Dashboard['daily'], limit: number | null }) {
    return (
        <div role="img" aria-label="Stacked bar chart of spoken summaries each day, split into those written fresh, which cost money, and those replayed from memory. The table view has the numbers.">
            <ResponsiveContainer width="100%" height={HEIGHT}>
                <BarChart data={daily} margin={{top: 8, right: 16, left: 0, bottom: 0}}>
                    {grid}{dayAxis}{countAxis}
                    <Legend verticalAlign="top" align="left" height={28} iconType="rect" iconSize={10} itemSorter={null} wrapperStyle={{fontSize: 13, color: INK_SECONDARY}}/>
                    <Tooltip content={<ChartTooltip/>} cursor={{fill: GRID}}/>
                    <Bar dataKey="summariesWritten" name="Written" stackId="summaries" fill={FIRST} stroke={SURFACE} strokeWidth={2} isAnimationActive={false}/>
                    <Bar dataKey="summariesReplayed" name="Replayed" stackId="summaries" fill={SECOND} stroke={SURFACE} strokeWidth={2} radius={[4, 4, 0, 0]} isAnimationActive={false}/>
                    {limit !== null && nearLimit(daily, limit) && (
                        <ReferenceLine y={limit} stroke={INK_SECONDARY} strokeDasharray="4 4" label={{value: `Daily limit ${limit}`, position: 'insideTopLeft', fill: INK_SECONDARY, fontSize: 12}}/>
                    )}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

export function ActivationChart({steps}: { steps: { label: string, people: number, share: number | null }[] }) {
    return (
        <div role="img" aria-label="Bar chart of how many of the people who signed up went on to each next step. The table view has the numbers.">
            <ResponsiveContainer width="100%" height={steps.length * 44 + 16}>
                <BarChart data={steps} layout="vertical" margin={{top: 0, right: 90, left: 0, bottom: 0}} barCategoryGap={10}>
                    <XAxis type="number" hide domain={[0, 'dataMax']}/>
                    <YAxis type="category" dataKey="label" width={130} {...AXIS} tick={{fill: INK_SECONDARY, fontSize: 13}}/>
                    <Tooltip cursor={{fill: GRID}} content={({active, payload}) => active && payload?.length
                        ? <ChartTooltip active payload={[{name: payload[0].payload.label, value: payload[0].value, color: FIRST}]} label=""/>
                        : null}/>
                    <Bar dataKey="people" name="People" fill={FIRST} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                        <LabelList dataKey="people" position="right" content={({x, y, width, height, index}) => {
                            const step = steps[Number(index)];
                            return step ? (
                                <text x={Number(x) + Number(width) + 8} y={Number(y) + Number(height) / 2} dy={4} fontSize={13} fill={INK_SECONDARY}>
                                    {formatNumber(step.people)}{step.share === null ? '' : ` · ${step.share}%`}
                                </text>
                            ) : null;
                        }}/>
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
